import {
  Box,
  Checkbox,
  DialogTitle,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import AbsenceHourRow from "./AbsenceHourRow";
import { Entry } from "../../../entriesAPI";
import { calculateTotalDuration } from "../../../../../app/common";
import { AssignmentById } from "../../../../talents/talentAPI";
import { useCallback, useMemo } from "react";
import { assignmentsAvailableForEntry } from "../../Shared/functions";
import { BANK_HOLIDAY_ABSENCE_TYPE } from "../../../constants";

export interface AbsenceListEntry extends Entry {
  checked: boolean;
}

interface Props {
  setEntryToEdit: (entry: AbsenceListEntry | null) => void;
  sortedEntries: AbsenceListEntry[];
  setEntries: (entries: AbsenceListEntry[]) => void;
  handleCheckboxUpdate: (entryId: string, checked: boolean) => void;
  absenceType?: string;
  inputErrors: { [code: string]: boolean };
  setInputErrors: (inputErrors: { [code: string]: boolean }) => void;
  talentId?: string;
  assignments: AssignmentById[];
  updateEntry: (updatedEntry: AbsenceListEntry) => void;
  createNewEntryRow: (start: number) => void;
  handleCheckAllToggle: (checked: boolean) => void;
}

const AbsenceHoursList = ({
  setEntryToEdit,
  sortedEntries,
  handleCheckboxUpdate,
  absenceType,
  inputErrors,
  setInputErrors,
  talentId,
  assignments,
  updateEntry,
  createNewEntryRow,
  handleCheckAllToggle,
}: Props) => {
  const { t } = useTranslation();

  // Memoize `setEntryToEdit` to prevent re-creating the function on every render
  const memoizedSetEntryToEdit = useCallback(
    (entry: AbsenceListEntry) => setEntryToEdit(entry),
    [setEntryToEdit],
  );

  const handleAllCheckedClick = useCallback(
    (checked: boolean) => {
      handleCheckAllToggle(checked);
    },
    [handleCheckAllToggle],
  );

  const allChecked = useMemo(() => {
    return (
      sortedEntries.length > 0 &&
      sortedEntries
        .filter((entry) => {
          // Filter entries that have assignments available
          const assignmentsForEntryDate: AssignmentById[] =
            assignmentsAvailableForEntry(assignments, entry);
          return assignmentsForEntryDate.length > 0 || !talentId; // Only include entries with available assignments
        })
        .every((entry) => entry.checked === true)
    );
  }, [sortedEntries, assignments, talentId]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        backgroundColor: "#FFFFFF",
        borderRadius: "8px",
        padding: "24px 16px",
        width: "75%",
        maxHeight: "100%",
        marginLeft: "16px",
      }}
    >
      <DialogTitle
        sx={{
          margin: "0px",
          padding: "0px",
        }}
      >
        <Typography
          variant="header"
          sx={{
            fontWeight: 600,
          }}
        >
          {t("hourEntries:absenceTimeWorkHours").toUpperCase()}
        </Typography>
        <Divider
          sx={{
            marginTop: "16px",
          }}
        />
        <TableContainer
          sx={{
            maxHeight: "360px",
            overflowY: "auto",
            overflowX: "hidden",
            marginRight: "16px",
          }}
        >
          <Table>
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    borderBottom: "none",
                    padding: "0px",
                    // If row is unchecked, enable pointer events to make checkbox clickable
                    ...(allChecked ? {} : { pointerEvents: "auto" }),
                  }}
                >
                  <Checkbox
                    checked={allChecked}
                    onChange={(e) => handleAllCheckedClick(e.target.checked)}
                    sx={{
                      padding: "0px",
                      marginRight: "8px",
                    }}
                  />
                </TableCell>
                <TableCell sx={{ borderBottom: "none" }}>
                  {t("hourEntries:dateLong")}
                </TableCell>
                <TableCell sx={{ borderBottom: "none", padding: "0px" }}>
                  {t("hourEntries:workInfo")}
                </TableCell>
                <TableCell sx={{ borderBottom: "none" }}>
                  {t("hourEntries:customerCostCenterName")}
                </TableCell>
                <TableCell sx={{ borderBottom: "none", padding: "0px" }}>
                  {t("hourEntries:startEnd")}
                </TableCell>
                <TableCell sx={{ borderBottom: "none", paddingRight: "8px" }}>
                  {t("hourEntries:duration")}
                </TableCell>
                <TableCell
                  sx={{ borderBottom: "none", padding: "0px" }}
                ></TableCell>
                <TableCell
                  sx={{ borderBottom: "none", padding: "0px" }}
                ></TableCell>
                {absenceType !== BANK_HOLIDAY_ABSENCE_TYPE && (
                  <TableCell sx={{ borderBottom: "none", padding: "0px" }}>
                    {t("hourEntries:notBilledShort")}
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedEntries.map((row) => (
                <AbsenceHourRow
                  key={row.id}
                  entry={row}
                  setEntryToEdit={memoizedSetEntryToEdit}
                  handleCheckboxUpdate={handleCheckboxUpdate}
                  inputErrors={inputErrors}
                  setInputErrors={setInputErrors}
                  talentId={talentId}
                  assignments={assignments}
                  updateEntry={updateEntry}
                  createNewEntryRow={createNewEntryRow}
                  absenceType={absenceType}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogTitle>

      <Divider
        sx={{
          marginTop: "24px",
          marginBottom: "24px",
        }}
      />

      <Box
        sx={{
          fontSize: "14px",
          fontWeight: 600,
          marginTop: "16px",
        }}
      >
        {`${t("hourEntries:absenceHours")}: ${calculateTotalDuration(sortedEntries.filter((entry) => entry.checked) as Entry[])}`}
      </Box>
    </Box>
  );
};

export default AbsenceHoursList;
